import { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Input, Button, notification, List, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useI18nLocalization } from '../../../i18nLocalization/useI18nLocalization';
import { useCategoriesLogic } from '../../useCategoriesLogic';
import { useApplicationsLogic } from '../../useApplicationsLogic';
import ConfirmDeleteModal from '../Applications/ConfirmDeleteModal';
import { categoryName } from '../../../validation/regex';

const EditCategoryModal = ({ isVisible, category, onCloseCallback, onSaveCallback, categoryNames }) => {
	const categoriesLogic = useCategoriesLogic();
	const applicationLogic = useApplicationsLogic();

	const { t } = useI18nLocalization();
	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);
	const [categoryApps, setCategoryApps] = useState(null);
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);

	useEffect(() => {
		if (!categoryApps) {
			getCategoryApps();
		}
	}, []);

	useEffect(() => {
		form.setFieldsValue({
			categoryName: category.name
		});
	}, [JSON.stringify(category)]);

	const getCategoryApps = async () => {
		const apps = await applicationLogic.getApplications();
		const filteredApps = apps.filter((app) => {
			if (app.categoryId === category.id) {
				return app;
			}
		});
		setCategoryApps(filteredApps);
	};

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const clearForm = () => {
		form.resetFields();
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const updatedCategory = getUpdatedCategory(formValues);
		const successful = await categoriesLogic.updateCategoryName(updatedCategory);
		if (successful) {
			notification['success']({
				message: category.name + ' ' + t('Updated') + '!',
				duration: 5
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t(
					`We encountered an error updating the category. Please try again. If it persists, please submit an EntDev Helpdesk ticket.`
				),
				duration: 10
			});
		}
		setIsSaving(false);
	};

	const handleDelete = async () => {
		setIsSaving(true);

		const successful = await categoriesLogic.deleteCategory(category.id);
		if (successful) {
			notification['success']({
				message: t(`${category.name} has been deleted`)
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t(
					`We encountered an error updating the category. Please try again. If it persists, please submit an EntDev Helpdesk ticket.`
				),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const getUpdatedCategory = (formValues) => {
		const newCategoryObject = {
			id: category.id,
			newName: formValues.categoryName
		};
		return newCategoryObject;
	};

	return (
		categoryApps && (
			<Modal
				visible={isVisible}
				title={t(`Edit ${category.name}`)}
				okText={t('Save')}
				onOk={() => {
					form.submit();
				}}
				cancelText={t('Cancel')}
				onCancel={handleOnCancel}
				forceRender
				confirmLoading={isSaving}
				footer={[
					<div key='delete-logic'>
						{!categoryApps.length ? (
							<Button
								type='danger'
								key='delete'
								style={{ float: 'left' }}
								onClick={() => {
									setDeleteModalVisible(true);
								}}
							>
								{t('Delete')}
							</Button>
						) : (
							<Tooltip title={t('CategoryDeleteTooltip')} key='delete-tooltip'>
								<Button type='danger' key='delete' style={{ float: 'left' }} disabled={true}>
									{t('Delete')}
								</Button>
							</Tooltip>
						)}
					</div>,
					<Button key='cancel' onClick={handleOnCancel}>
						{t('Cancel')}
					</Button>,
					<Button
						key='save'
						onClick={() => {
							form.submit();
						}}
					>
						{t('Save')}
					</Button>
				]}
			>
				<ConfirmDeleteModal
					deleteModalIsVisible={deleteModalVisible}
					onConfirmDeleteCallback={() => {
						handleDelete();
						setDeleteModalVisible(false);
						onSaveCallback();
					}}
					onCancelDeleteCallback={() => {
						setDeleteModalVisible(false);
					}}
					applicationName={category.name}
				></ConfirmDeleteModal>
				<Form form={form} layout='vertical' requiredMark scrollToFirstError onFinish={handleOnSave}>
					<Row>
						<Col span={24}>
							<Form.Item
								label={t('Category Name')}
								name='categoryName'
								rules={[
									{
										validator: (field, value) => {
											if (!value) {
												return Promise.reject(new Error(t('Name is required')));
											}
											if (value != category.name && categoryNames.includes(value)) {
												return Promise.reject(new Error(t('Name cannot be used by another category')));
											}
											if (!value.match(categoryName)) {
												return Promise.reject(new Error(t('Name must be alpha characters')));
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<Input placeholder={t('category name')} maxLength={50} />
							</Form.Item>
						</Col>
					</Row>
				</Form>
				<List
					size='small'
					header={t('Apps currently in category')}
					dataSource={categoryApps}
					renderItem={(item) => (
						<List.Item key={item.id}>
							<List.Item.Meta title={item.name} />
						</List.Item>
					)}
				></List>
			</Modal>
		)
	);
};

EditCategoryModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onCloseCallback: PropTypes.func.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	category: PropTypes.object.isRequired,
	categoryNames: PropTypes.array.isRequired
};

export default EditCategoryModal;
